import React, { useMemo, useState, createContext, useContext, ReactNode } from 'react';

import { enqueueSnackbar } from 'notistack';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';

import { deleteItemByField } from '../../../../../shared/utils/arrayUtils';
import { videosServices } from '../../../../services/videosServices';
import {
  UpdateVideo,
  Video,
  VideoCategory,
  VideoFormValues,
} from '../../../../interface/video';
import { Option } from '../../../../../general-types';
import { openModal } from '../../../../../shared/utils/reactUtils/openModal/openModal';
import { VideoFormModal } from '../VideoFormModal/VideoFormModal';
import { VideoPageType } from '../../../../constants/videoPage';

export type VideoByCategory = Record<string, Video[]>;

export type CategoryOption = Option<string> & { icon?: ReactNode };

type UseVideosManagementReturn = {
  shownVideos: Video[];
  categoryOptions: CategoryOption[];
  selectedCategory: string | null;
  getVideos: () => Promise<void>;
  changeSelectedCategory: (category: string) => void;
  openAddVideoModal: () => void;
  openEditVideoModal: (video: Video) => void;
  deleteVideo: (id: number) => Promise<void>;
  reorderVideos: (reorderedVideos: Video[]) => Promise<void>;
};

export const useVideosManagement = (page: VideoPageType): UseVideosManagementReturn => {
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [videoByCategory, setVideoByCategory] = useState<VideoByCategory | null>(null);

  const processVideoData = (videoData: VideoCategory[]): VideoByCategory =>
    videoData.reduce((acc, category) => {
      acc[category.name] = category.videos;
      return acc;
    }, {} as VideoByCategory);

  const getVideosByCategory = (category: string | null): Video[] => {
    if (!category || !videoByCategory) {
      throw new Error('No necessary data');
    }

    return videoByCategory[category] || [];
  };

  const setDefaultVideoPosition = (video: VideoFormValues | UpdateVideo): UpdateVideo => {
    const videos = getVideosByCategory(video.category);

    return {
      ...video,
      position: videos[videos.length - 1].position + 1,
    };
  };

  const getVideos = async () => {
    const { categories } = await videosServices.getVideosBySuper(page);
    const categoriesOptions: CategoryOption[] = categories.map((category) => ({
      value: category.name,
      label: category.name,
      icon: category.featuredCategory ? <StarBorderOutlinedIcon /> : undefined,
    }));

    setCategoryOptions(categoriesOptions);
    setSelectedCategory(categoriesOptions[0].value || null);
    setVideoByCategory(processVideoData(categories));
  };

  const deleteVideo = async (id: number) => {
    await videosServices.deleteVideoById(id);
    const videos = getVideosByCategory(selectedCategory);
    const newVideos = deleteItemByField(videos, { id });

    await reorderVideos(newVideos);
  };

  const openAddVideoModal = () => {
    const videoFormValues: VideoFormValues = {
      title: '',
      youtubevideo: '',
      isDisplayed: false,
      category: selectedCategory!,
    };

    const handleAddVideo = async (video: VideoFormValues) => {
      const newVideo = setDefaultVideoPosition(video);

      await handleUpdateVideos([newVideo]);
      enqueueSnackbar('Video uploaded successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      });
    };

    openModal((closeModal) => (
      <VideoFormModal
        title="Add New Video"
        video={videoFormValues}
        categoryOptions={categoryOptions}
        onSubmit={handleAddVideo}
        onClose={closeModal}
      />
    ));
  };

  const openEditVideoModal = (video: Video) => {
    const videoFormValues: VideoFormValues = {
      title: video.title,
      youtubevideo: video.youtubevideo,
      isDisplayed: video.isDisplayed,
      category: selectedCategory!,
    };

    const getUpdatedVideo = async (editedVideo: VideoFormValues) => {
      const updatedVideo: UpdateVideo = {
        ...video,
        ...editedVideo,
      };

      if (editedVideo.category !== selectedCategory) {
        return setDefaultVideoPosition(updatedVideo);
      } else {
        return updatedVideo;
      }
    };

    const handelEditVideo = async (editedVideo: VideoFormValues) => {
      const updatedVideo = await getUpdatedVideo(editedVideo);

      await handleUpdateVideos([updatedVideo]);
      enqueueSnackbar('Video updated successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      });
    };

    openModal((closeModal) => (
      <VideoFormModal
        title="Edit Video"
        video={videoFormValues}
        categoryOptions={categoryOptions}
        onSubmit={handelEditVideo}
        onClose={closeModal}
      />
    ));
  };

  const handleUpdateVideos = async (videos: UpdateVideo[]) => {
    const { categories } = await videosServices.updateVideos({ videos }, page);

    setVideoByCategory(processVideoData(categories));
  };

  /**
   * @param reorderedVideos - videos in correct order without correct position
   */
  const reorderVideos = async (reorderedVideos: Video[]) => {
    if (!selectedCategory) {
      throw new Error('No category selected');
    }

    const withCorrectPosition = reorderedVideos.map((video, index) => ({
      ...video,
      position: index,
      category: selectedCategory,
    }));

    // optimistic update need to set correct position after drop
    setVideoByCategory({
      ...videoByCategory,
      [selectedCategory]: withCorrectPosition,
    });

    await handleUpdateVideos(withCorrectPosition);
  };

  const shownVideos = useMemo(() => {
    return videoByCategory ? getVideosByCategory(selectedCategory) : [];
  }, [videoByCategory, selectedCategory]);

  return {
    shownVideos,
    categoryOptions,
    selectedCategory,
    changeSelectedCategory: setSelectedCategory,
    getVideos,
    openAddVideoModal,
    openEditVideoModal,
    deleteVideo,
    reorderVideos,
  };
};

const VideosManagementContext = createContext<UseVideosManagementReturn | null>(null);

type VideosManagementProviderProps = {
  value: UseVideosManagementReturn;
  children: React.ReactNode;
};

export const VideosManagementProvider = ({
  value,
  children,
}: VideosManagementProviderProps) => {
  return <VideosManagementContext value={value}>{children}</VideosManagementContext>;
};

export const useVideosManagementContext = () => {
  const context = useContext(VideosManagementContext);

  if (!context) {
    throw new Error(
      'useVideosManagementContext must be used within a VideosManagementProvider',
    );
  }

  return context;
};
